import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Ticket, Bell, User, LogOut, Trophy, PartyPopper, X, KeyRound, Eye, EyeOff, QrCode, Phone, CalendarDays, ChevronRight, BookOpen, Trash2, CheckSquare, Square, CheckCheck } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import TicketCard from '../components/TicketCard';
import WinnerModal from '../components/WinnerModal';
import LiveDrawOverlay from '../components/LiveDrawOverlay';
import ReferralPanel from '../components/ReferralPanel';
import { AppShell, IconButton } from '../components/layout/AppShell';
import { Card, EmptyState, SectionHeader } from '../components/layout/Primitives';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../components/ui/alert-dialog';
import { PullToRefresh } from '../components/layout/PullToRefresh';
import { UserMenusTab } from '../components/menu/UserMenus';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOLD = '#EAB308';

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

const PasswordField = ({ id, label, testId, value, onChange, show, onToggle, placeholder }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-[#a1a1aa]">{label}</Label>
    <div className="relative">
      <Input
        id={id}
        data-testid={testId}
        type={show ? 'text' : 'password'}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required
        className="h-12 rounded-xl border-white/[0.08] bg-[#09090b] pr-11 text-white placeholder:text-[#52525b] focus-visible:ring-[#EAB308]"
      />
      {onToggle && (
        <button type="button" onClick={onToggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#52525b] hover:text-white" aria-label="Toggle visibility">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      )}
    </div>
  </div>
);

const UserDashboard = ({ user, onLogout }) => {
  const [tickets, setTickets] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [winner, setWinner] = useState(null);
  const [activeTab, setActiveTab] = useState('tickets');
  const [userProfile, setUserProfile] = useState(null);
  const [liveDrawId, setLiveDrawId] = useState(null);
  const [acknowledgedDraws, setAcknowledgedDraws] = useState(() => {
    const saved = localStorage.getItem('acknowledgedLiveDraws');
    return saved ? JSON.parse(saved) : [];
  });
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedNotifs, setSelectedNotifs] = useState([]);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [showPast, setShowPast] = useState(() => localStorage.getItem('showPastTickets') !== 'false');

  const token = localStorage.getItem('token');

  const isPastTicket = (t) => {
    const s = t.draw?.status;
    return s === 'completed' || s === 'deleted' || t.draw?.deleted === true || t.draw_deleted === true;
  };
  const activeTickets = tickets.filter((t) => !isPastTicket(t));
  const pastTickets = tickets.filter(isPastTicket);
  const togglePast = () => {
    setShowPast((v) => { localStorage.setItem('showPastTickets', String(!v)); return !v; });
  };

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API}/user/tickets`, { headers: { Authorization: `Bearer ${token}` } });
      const filteredTickets = response.data
        .filter((ticket) => ticket.draw)
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      setTickets(filteredTickets);

      const wonTicket = filteredTickets.find(
        (ticket) => ticket.draw?.winner_id === user.id && (ticket.draw?.status === 'completed' || ticket.draw?.status === 'deleted')
      );
      if (wonTicket) {
        const winnerModalShown = localStorage.getItem(`winner_modal_shown_${wonTicket.draw.id}_${user.id}`);
        if (!winnerModalShown) setWinner({ draw: wonTicket.draw, ticket: wonTicket });
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/user/notifications`, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/user/profile`, { headers: { Authorization: `Bearer ${token}` } });
      setUserProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.newPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(
        `${API}/user/change-password`,
        { current_password: passwordData.currentPassword, new_password: passwordData.newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowChangePassword(false);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const checkForLiveDraws = useCallback(async () => {
    try {
      const ticketsResponse = await axios.get(`${API}/user/tickets`, { headers: { Authorization: `Bearer ${token}` } });
      const userDrawIds = [...new Set(ticketsResponse.data.map((t) => t.draw_id))];
      for (const drawId of userDrawIds) {
        try {
          const statusResponse = await axios.get(`${API}/draws/${drawId}/live-status`);
          const status = statusResponse.data.status;
          if ((status === 'countdown' || status === 'spinning') && !acknowledgedDraws.includes(drawId)) {
            setLiveDrawId(drawId);
            toast.info('A LIVE DRAW is starting!', { duration: 3000 });
            break;
          }
        } catch (err) {
          // draw may not exist
        }
      }
    } catch (error) {
      console.error('Error checking for live draws:', error);
    }
  }, [token, acknowledgedDraws]);

  const handleCloseLiveDraw = () => {
    if (liveDrawId) {
      const updated = [...acknowledgedDraws, liveDrawId];
      setAcknowledgedDraws(updated);
      localStorage.setItem('acknowledgedLiveDraws', JSON.stringify(updated));
    }
    setLiveDrawId(null);
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTickets(), fetchNotifications(), fetchProfile()]);
      setLoading(false);
    };
    loadData();
    const interval = setInterval(() => { fetchTickets(); fetchNotifications(); fetchProfile(); }, 10000);
    const liveDrawInterval = setInterval(() => checkForLiveDraws(), 3000);
    checkForLiveDraws();
    return () => { clearInterval(interval); clearInterval(liveDrawInterval); };
  }, [checkForLiveDraws]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/user/notifications/${notificationId}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications((prev) => prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n)));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const deleteNotification = async (notificationId, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    try {
      await axios.delete(`${API}/user/notifications/${notificationId}`, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      toast.success('Notification deleted');
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const markAllRead = async () => {
    if (!notifications.some((n) => !n.read)) return;
    try {
      await axios.post(`${API}/user/notifications/mark-all-read`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      toast.success('All marked as read');
    } catch (error) {
      toast.error('Failed to update notifications');
    }
  };

  const toggleSelectNotif = (id) => {
    setSelectedNotifs((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedNotifs([]);
  };

  const deleteSelectedNotifs = async () => {
    if (selectedNotifs.length === 0) return;
    const ids = selectedNotifs;
    try {
      await axios.post(`${API}/user/notifications/delete-batch`, { ids }, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications((prev) => prev.filter((n) => !ids.includes(n.id)));
      toast.success(`Deleted ${ids.length} notification${ids.length === 1 ? '' : 's'}`);
      exitSelectMode();
    } catch (error) {
      toast.error('Failed to delete notifications');
    }
  };

  const clearAllNotifs = async () => {
    if (notifications.length === 0) return;
    try {
      await axios.delete(`${API}/user/notifications`, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications([]);
      toast.success('All notifications cleared');
      exitSelectMode();
    } catch (error) {
      toast.error('Failed to clear notifications');
    } finally {
      setClearDialogOpen(false);
    }
  };

  const refreshTickets = useCallback(async () => {
    await Promise.all([fetchTickets(), fetchProfile()]);
    setRefreshKey((k) => k + 1);
  }, []);

  const refreshNotifications = useCallback(async () => {
    await fetchNotifications();
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;
  const firstName = (user.name || '').split(' ')[0];
  const initials = (user.name || '?').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#09090b]">
        <div className="animate-pulse font-heading text-sm uppercase tracking-widest text-[#EAB308]">Loading your tickets</div>
      </div>
    );
  }

  const tabs = [
    { value: 'tickets', label: 'My Tickets', shortLabel: 'Tickets', icon: Ticket, badge: activeTickets.length },
    { value: 'notifications', label: 'Notifications', shortLabel: 'Alerts', icon: Bell, badge: unreadCount },
    { value: 'menu', label: 'Menu', icon: BookOpen },
    { value: 'myqr', label: 'My QR', icon: QrCode },
    { value: 'profile', label: 'Profile', icon: User },
  ];

  const pageMeta = {
    tickets: { title: `${greeting()}, ${firstName}`, subtitle: activeTickets.length ? `You're in ${activeTickets.length} live giveaway${activeTickets.length === 1 ? '' : 's'}` : tickets.length ? `${tickets.length} ticket${tickets.length === 1 ? '' : 's'} in total` : 'No tickets yet' },
    notifications: { title: 'Notifications', subtitle: unreadCount ? `${unreadCount} unread` : "You're all caught up" },
    menu: { title: 'Menu', subtitle: 'Menus, deals & offers' },
    myqr: { title: 'My QR', subtitle: 'Show this at an event to get a ticket' },
    profile: { title: 'Profile', subtitle: 'Your account & stats' },
  }[activeTab];

  return (
    <>
      {liveDrawId && <LiveDrawOverlay drawId={liveDrawId} userId={user.id} onClose={handleCloseLiveDraw} />}
      <WinnerModal
        winner={winner}
        onClose={() => {
          if (winner) localStorage.setItem(`winner_modal_shown_${winner.draw.id}_${user.id}`, 'true');
          setWinner(null);
        }}
      />

      <AppShell
        accent={GOLD}
        accentFg="text-black"
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        pageTitle={pageMeta.title}
        pageSubtitle={pageMeta.subtitle}
        headerRight={
          <>
            <button
              type="button"
              onClick={() => setActiveTab('profile')}
              data-testid="header-avatar-btn"
              className="tap-none flex h-9 w-9 items-center justify-center rounded-full bg-[#EAB308]/15 font-heading text-xs font-semibold text-[#EAB308]"
            >
              {initials}
            </button>
            <IconButton data-testid="logout-btn" onClick={onLogout} aria-label="Log out">
              <LogOut className="h-4 w-4" />
            </IconButton>
          </>
        }
      >
        {activeTab === 'tickets' && (
          <PullToRefresh onRefresh={refreshTickets} accent={GOLD}>
          <div data-testid="tickets-content" className="space-y-6">
            <ReferralPanel refreshKey={refreshKey} />
            <div>
              <SectionHeader overline="Active" title="Your tickets" />
              {activeTickets.length === 0 ? (
                <EmptyState icon={Ticket} title="No active tickets" message="Check back soon for new giveaways, or show your QR at an event." />
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {activeTickets.map((ticket, index) => (
                    <TicketCard
                      key={ticket.id}
                      ticket={ticket}
                      delay={Math.min(index, 6) * 0.06}
                      onDelete={(ticketId) => { setTickets((prev) => prev.filter((t) => t.id !== ticketId)); fetchProfile(); }}
                    />
                  ))}
                </div>
              )}
            </div>
            {pastTickets.length > 0 && (
              <div data-testid="past-tickets-section">
                <SectionHeader
                  overline="Finished"
                  title="Past giveaways"
                  subtitle={`${pastTickets.length} ticket${pastTickets.length === 1 ? '' : 's'}`}
                  action={
                    <button
                      type="button"
                      onClick={togglePast}
                      data-testid="toggle-past-tickets-btn"
                      className="tap-none inline-flex items-center gap-1 rounded-full border border-white/[0.1] px-3 py-1.5 text-xs font-semibold text-[#a1a1aa] transition-colors hover:border-[#EAB308]/50 hover:text-white"
                    >
                      {showPast ? 'Hide' : 'Show'}
                      <ChevronRight className={`h-3.5 w-3.5 transition-transform ${showPast ? 'rotate-90' : ''}`} />
                    </button>
                  }
                />
                {showPast && (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {pastTickets.map((ticket, index) => (
                      <TicketCard
                        key={ticket.id}
                        ticket={ticket}
                        delay={Math.min(index, 6) * 0.04}
                        onDelete={(ticketId) => { setTickets((prev) => prev.filter((t) => t.id !== ticketId)); fetchProfile(); }}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          </PullToRefresh>
        )}

        {activeTab === 'notifications' && (
          <PullToRefresh onRefresh={refreshNotifications} accent={GOLD}>
          <div data-testid="notifications-content">
            {notifications.length === 0 ? (
              <EmptyState icon={Bell} title="No notifications" message="We'll let you know when a draw goes live or you win." />
            ) : (
              <>
                {/* Bulk actions bar */}
                <div className="mb-3 flex items-center justify-between gap-2">
                  {selectMode ? (
                    <>
                      <button
                        onClick={exitSelectMode}
                        data-testid="notif-cancel-select-btn"
                        className="tap-none rounded-full border border-white/[0.1] px-3 py-1.5 text-xs font-medium text-[#a1a1aa] hover:text-white"
                      >
                        Cancel
                      </button>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedNotifs(selectedNotifs.length === notifications.length ? [] : notifications.map((n) => n.id))}
                          data-testid="notif-select-all-btn"
                          className="tap-none rounded-full border border-white/[0.1] px-3 py-1.5 text-xs font-medium text-[#a1a1aa] hover:text-white"
                        >
                          {selectedNotifs.length === notifications.length ? 'Deselect all' : 'Select all'}
                        </button>
                        <button
                          onClick={deleteSelectedNotifs}
                          disabled={selectedNotifs.length === 0}
                          data-testid="notif-delete-selected-btn"
                          className="tap-none inline-flex items-center gap-1.5 rounded-full bg-[#f43f5e] px-3.5 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Delete{selectedNotifs.length ? ` (${selectedNotifs.length})` : ''}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setSelectMode(true)}
                        data-testid="notif-select-btn"
                        className="tap-none inline-flex items-center gap-1.5 rounded-full border border-white/[0.1] px-3 py-1.5 text-xs font-medium text-[#a1a1aa] hover:text-white"
                      >
                        <CheckSquare className="h-3.5 w-3.5" /> Select
                      </button>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={markAllRead}
                          data-testid="notif-mark-all-read-btn"
                          className="tap-none inline-flex items-center gap-1.5 rounded-full border border-white/[0.1] px-3 py-1.5 text-xs font-medium text-[#a1a1aa] hover:text-white"
                        >
                          <CheckCheck className="h-3.5 w-3.5" /> Mark all read
                        </button>
                        <button
                          onClick={() => setClearDialogOpen(true)}
                          data-testid="notif-clear-all-btn"
                          className="tap-none inline-flex items-center gap-1.5 rounded-full border border-[#f43f5e]/40 px-3 py-1.5 text-xs font-medium text-[#f43f5e] hover:bg-[#f43f5e]/10"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Clear all
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <Card className="divide-y divide-white/[0.06] overflow-hidden">
                {notifications.map((notification) => {
                  const isSelected = selectedNotifs.includes(notification.id);
                  return (
                  <div key={notification.id} className="relative overflow-hidden">
                    {!selectMode && (
                      <div className="pointer-events-none absolute inset-0 flex items-center justify-end bg-[#f43f5e] pr-6">
                        <div className="flex items-center gap-1.5 text-white">
                          <Trash2 className="h-4 w-4" />
                          <span className="text-xs font-semibold">Delete</span>
                        </div>
                      </div>
                    )}
                    <motion.div
                      drag={selectMode ? false : 'x'}
                      dragDirectionLock
                      dragConstraints={{ left: 0, right: 0 }}
                      dragElastic={{ left: 0.8, right: 0 }}
                      onDragEnd={(e, info) => {
                        if (!selectMode && info.offset.x < -90) deleteNotification(notification.id);
                      }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1, x: 0 }}
                      onClick={() => {
                        if (selectMode) { toggleSelectNotif(notification.id); return; }
                        if (!notification.read) markAsRead(notification.id);
                        if (notification.type === 'menu_published') setActiveTab('menu');
                      }}
                      data-testid={`notification-${notification.id}`}
                      className={`relative flex items-start gap-3 px-4 py-4 ${selectMode && isSelected ? 'bg-[#211417]' : 'bg-[#18181b]'} ${!selectMode ? 'cursor-grab active:cursor-grabbing' : ''}`}
                    >
                    {selectMode && (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleSelectNotif(notification.id); }}
                        data-testid={`notif-checkbox-${notification.id}`}
                        className="mt-0.5 shrink-0 text-[#a1a1aa]"
                        aria-label={isSelected ? 'Deselect' : 'Select'}
                      >
                        {isSelected ? <CheckSquare className="h-5 w-5 text-[#f43f5e]" /> : <Square className="h-5 w-5" />}
                      </button>
                    )}
                    <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${notification.type === 'winner' ? 'bg-[#EAB308]/20' : notification.type === 'menu_published' ? 'bg-[#10b981]/20' : 'bg-[#A855F7]/20'}`}>
                      {notification.type === 'winner' ? <PartyPopper className="h-4 w-4 text-[#EAB308]" /> : notification.type === 'menu_published' ? <BookOpen className="h-4 w-4 text-[#10b981]" /> : <Bell className="h-4 w-4 text-[#A855F7]" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm leading-relaxed ${notification.read ? 'text-[#d4d4d8]' : 'font-medium text-white'}`}>{notification.message}</p>
                      <p className="mt-1 text-xs text-[#71717a]">
                        {new Date(notification.created_at).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    {!selectMode && (
                      <div className="flex shrink-0 items-center gap-2">
                        {notification.type === 'menu_published' && <ChevronRight className="h-4 w-4 text-[#52525b]" />}
                        {!notification.read && <span className="h-2 w-2 rounded-full bg-[#EAB308]" />}
                        <button
                          onClick={(e) => deleteNotification(notification.id, e)}
                          data-testid={`delete-notification-btn-${notification.id}`}
                          aria-label="Delete notification"
                          className="tap-none flex h-8 w-8 items-center justify-center rounded-full text-[#52525b] hover:bg-[#f43f5e]/15 hover:text-[#f43f5e]"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                    </motion.div>
                  </div>
                  );
                })}
                </Card>
                <AlertDialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
                  <AlertDialogContent data-testid="clear-all-dialog" className="border-white/10 bg-[#18181b] text-white">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Clear all notifications?</AlertDialogTitle>
                      <AlertDialogDescription className="text-[#a1a1aa]">
                        This will permanently remove all {notifications.length} notification{notifications.length === 1 ? '' : 's'}. This can't be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="clear-all-cancel-btn" className="border-white/10 bg-transparent text-white hover:bg-white/[0.06]">Cancel</AlertDialogCancel>
                      <AlertDialogAction data-testid="clear-all-confirm-btn" onClick={clearAllNotifs} className="bg-[#f43f5e] text-white hover:bg-[#e11d48]">
                        Clear all
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
          </div>
          </PullToRefresh>
        )}

        {activeTab === 'menu' && (
          <div data-testid="menu-content">
            <UserMenusTab />
          </div>
        )}

        {activeTab === 'myqr' && (
          <div data-testid="myqr-content" className="mx-auto max-w-sm">
            <Card className="overflow-hidden">
              <div className="flex flex-col items-center px-6 pt-8 pb-6">
                <div className="rounded-2xl bg-white p-4 shadow-[0_0_40px_-10px_rgba(234,179,8,0.45)]" data-testid="user-qr-code">
                  <QRCodeSVG value={`RAFFLESRUS-USER:${user.id}`} size={200} level="M" />
                </div>
                <p className="mt-6 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#71717a]">Member</p>
                <p className="mt-1 font-heading text-lg font-semibold text-white">{user.name}</p>
                <p className="mt-0.5 text-sm text-[#a1a1aa]">{user.whatsapp_number}</p>
              </div>
              <div className="border-t border-white/[0.06] bg-white/[0.02] px-6 py-4">
                <p className="text-center text-sm leading-relaxed text-[#a1a1aa]">
                  Staff scan this code to add a raffle ticket straight to your account.
                </p>
              </div>
            </Card>
          </div>
        )}

        {activeTab === 'profile' && (
          <div data-testid="profile-content" className="mx-auto max-w-2xl space-y-4">
            <Card className="p-5">
              <div className="flex items-center gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#EAB308]/15 font-heading text-lg font-semibold text-[#EAB308]">
                  {initials}
                </div>
                <div className="min-w-0">
                  <h2 className="truncate font-heading text-lg font-semibold text-white">{user.name}</h2>
                  <p className="text-sm text-[#a1a1aa]">Member since {new Date(user.created_at).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}</p>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-3 divide-x divide-white/[0.06] rounded-xl bg-[#09090b]/60">
                {[
                  { label: 'Active', value: userProfile?.active_tickets || 0, color: '#06b6d4' },
                  { label: 'Total', value: userProfile?.total_tickets || 0, color: '#A855F7' },
                  { label: 'Wins', value: userProfile?.times_won || 0, color: GOLD },
                ].map((s) => (
                  <div key={s.label} className="px-3 py-4 text-center">
                    <div className="font-heading text-xl font-semibold text-white tabular sm:text-2xl" style={{ color: s.value > 0 && s.label === 'Wins' ? GOLD : undefined }}>{s.value}</div>
                    <div className="mt-0.5 text-[11px] font-medium uppercase tracking-wider text-[#71717a]">{s.label}</div>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="divide-y divide-white/[0.06]">
              <div className="flex items-center gap-3 px-5 py-4">
                <Phone className="h-4 w-4 text-[#71717a]" />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-medium uppercase tracking-wider text-[#71717a]">WhatsApp</p>
                  <p className="truncate text-sm font-medium text-white">{user.whatsapp_number}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 px-5 py-4">
                <CalendarDays className="h-4 w-4 text-[#71717a]" />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-medium uppercase tracking-wider text-[#71717a]">Joined</p>
                  <p className="text-sm font-medium text-white">{new Date(user.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            </Card>
          </div>
        )}
      </AppShell>
    </>
  );
};

export default UserDashboard;
